// Manage Portfolio: the mandate checker for every analyst book (vs DS70's current
// approved set), then an editor for one book — change weights, add or remove funds,
// pick the effective date, save. Every check runs on the server (the same code as the
// save gate): weights total 100.00, each fund on the AP List exactly once, the date
// between the book's latest saved set and today, and every sub-asset class plus growth
// within ±5.00pp of DS70. A breaching book cannot be saved.
import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { API, apiError, fmtDate, fmtPct, getAssetClassColor, useApiGet } from './apUtils';
import { BookSelect, Notice } from './apComponents';

const sydneyToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' });
const num = (s) => { const n = Number(s); return s === '' || !Number.isFinite(n) ? null : n; };

// The book's latest saved set as editable lines, from the holdings-history payload.
function baseFrom(hh) {
  const latest = hh.dates[0];
  const lines = [];
  hh.asset_classes.forEach((ac) => ac.holdings.forEach((h) => {
    if (h.values[latest] != null) {
      lines.push({ symbol: h.tr_code, name: h.asset_name, apir: h.apir_code, acg: ac.name,
        current: h.values[latest], weight: String(h.values[latest]) });
    }
  }));
  return { key: `${hh.code}|${latest || ''}|${lines.length}`, latest, lines };
}

function MandatePanel({ state }) {
  if (state.error) return <Notice kind="error">Mandate check failed: {state.error}</Notice>;
  if (!state.data) return <div className="chart-loading">Checking every book against DS70...</div>;
  const { baseline, books, limit_pp: limit } = state.data;
  return (
    <div className="ap-mandate">
      <h4 className="ap-section-title">Mandate checker</h4>
      <p className="ap-footnote">
        Every sub-asset class and the growth exposure must sit within ±{limit.toFixed(2)}pp of DS70&apos;s current approved
        set (v{baseline.version}, effective {fmtDate(baseline.effective_from)}; growth {baseline.growth.toFixed(2)}%).
        Exactly {limit.toFixed(2)}pp passes. Shown for each book&apos;s holdings in force today.
      </p>
      {baseline.errors?.length > 0 && <Notice kind="error">{baseline.errors.join(' · ')}</Notice>}
      <table className="ap-deck-table ap-mandate-table">
        <thead>
          <tr><th className="ap-deck-name">Book</th><th>Holdings from</th><th>Growth</th><th>vs DS70</th>
            <th>Largest SAC gap</th><th>Status</th><th className="ap-l">Breaches</th></tr>
        </thead>
        <tbody>
          {books.map((b) => {
            const worst = b.rows.reduce((m, r) => (Math.abs(r.diff) > Math.abs(m?.diff ?? 0) ? r : m), null);
            return (
              <tr key={b.code}>
                <td className="ap-deck-name">{b.name}</td>
                <td>{fmtDate(b.as_at)}</td>
                <td>{b.growth ? fmtPct(b.growth.book, 2) : '–'}</td>
                <td>{b.growth ? fmtPct(b.growth.diff, 2, true) : '–'}</td>
                <td>{worst ? `${worst.sub_asset_class} ${fmtPct(worst.diff, 2, true)}` : '–'}</td>
                <td><span className={`ap-pill ${b.ok ? 'ap-pill-ok' : 'ap-pill-bad'}`}>{b.ok ? 'Within limits' : 'Breach'}</span></td>
                <td className="ap-l">{[...b.breaches, ...b.errors].join(' · ') || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CheckTable({ check }) {
  return (
    <table className="ap-deck-table ap-check-table">
      <thead>
        <tr><th className="ap-deck-name">Sub-asset class</th><th>Book</th><th>DS70</th><th>Difference</th>
          <th>Allowed</th><th>Status</th></tr>
      </thead>
      <tbody>
        {check.rows.map((r) => (
          <tr key={r.sub_asset_class} className={r.ok ? undefined : 'ap-breach'}>
            <td className="ap-deck-name">{r.sub_asset_class}</td>
            <td>{fmtPct(r.book, 2)}</td><td>{fmtPct(r.ds70, 2)}</td><td>{fmtPct(r.diff, 2, true)}</td>
            <td>{fmtPct(r.min, 2)} – {fmtPct(r.max, 2)}</td>
            <td>{r.ok ? '✓' : '✗'}</td>
          </tr>
        ))}
        <tr className={`ap-check-growth${check.growth.ok ? '' : ' ap-breach'}`}>
          <td className="ap-deck-name">Growth exposure</td>
          <td>{fmtPct(check.growth.book, 2)}</td><td>{fmtPct(check.growth.ds70, 2)}</td>
          <td>{fmtPct(check.growth.diff, 2, true)}</td>
          <td>{fmtPct(check.growth.min, 2)} – {fmtPct(check.growth.max, 2)}</td>
          <td>{check.growth.ok ? '✓' : '✗'}</td>
        </tr>
      </tbody>
    </table>
  );
}

/** A fund's fee (% p.a.) with its source on hover, flagged when there is none, and an
    inline edit that sets the fee in this app (or clears this app's fee, falling back to the
    next source). Cash carries no fee; a fund with no APIR cannot be looked up or set. */
function FeeCell({ info, apir, symbol, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (!info) return <td className="ap-fee">…</td>;
  if (info.fee_source === 'cash') return <td className="ap-fee" title={info.fee_label}>—</td>;
  const put = (fee) => {
    setBusy(true);
    setErr(null);
    api.put(`${API}/fund-fees/${encodeURIComponent(apir)}`, { fee_pct: fee, symbol })
      .then(() => { setEditing(false); onChanged(); })
      .catch((e) => setErr(apiError(e)))
      .finally(() => setBusy(false));
  };
  if (editing) {
    const n = Number(val);
    const ok = val !== '' && Number.isFinite(n) && n >= 0 && n <= 10;
    return (
      <td className="ap-fee ap-fee-editing">
        <input className="ap-fee-input" type="number" step="0.01" min="0" max="10" value={val} autoFocus
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && ok) put(n); if (e.key === 'Escape') setEditing(false); }} />
        <button type="button" className="ap-fee-btn" disabled={!ok || busy} title="Save this fee" onClick={() => put(n)}>✓</button>
        {info.fee_source === 'app' && (
          <button type="button" className="ap-fee-btn" disabled={busy} title="Clear this app's fee (use the next source)"
            onClick={() => put(null)}>↺</button>
        )}
        <button type="button" className="ap-fee-btn" disabled={busy} title="Cancel" onClick={() => setEditing(false)}>✕</button>
        {err && <div className="ap-fee-err">{err}</div>}
      </td>
    );
  }
  return (
    <td className={`ap-fee${info.fee == null ? ' ap-fee-missing' : ''}`} title={info.fee_label}>
      {info.fee == null ? <span className="ap-flag ap-flag-nofee">No fee</span> : `${info.fee.toFixed(2)}%`}
      {info.fee_source === 'app' && <span className="ap-fee-own">set here</span>}
      {apir && (
        <button type="button" className="ap-fee-edit" title="Set this fund's fee in this app"
          onClick={() => { setVal(info.fee == null ? '' : String(info.fee)); setErr(null); setEditing(true); }}>✎</button>
      )}
    </td>
  );
}

export default function ManagePortfolioTab({ books, onSaved, onFeesChanged }) {
  const analyst = useMemo(() => books.filter((b) => b.kind === 'analyst'), [books]);
  const [code, setCode] = useState(analyst[0]?.code || '');
  const [reload, setReload] = useState(0);
  const mandate = useApiGet(`${API}/mandate-check`, null, reload);
  const funds = useApiGet(`${API}/funds`);
  const hist = useApiGet(code ? `${API}/portfolios/${code}/holdings-history` : null, null, reload);
  const base = useMemo(() => (hist.data ? baseFrom(hist.data) : null), [hist.data]);

  // Edits live in `draft` only while it belongs to the loaded set (same key); otherwise
  // the saved set shows, so switching book or reloading after a save starts clean.
  const [draft, setDraft] = useState(null);
  const active = draft && base && draft.key === base.key ? draft : null;
  const lines = useMemo(() => (active ? active.lines : base?.lines || []), [active, base]);
  const date = active?.date ?? sydneyToday();
  const note = active?.note ?? '';
  const edit = (fn) => setDraft((d) => {
    const cur = d && base && d.key === base.key ? d : { key: base.key, lines: base.lines, date: sydneyToday(), note: '' };
    return { ...cur, ...fn(cur) };
  });

  const [q, setQ] = useState('');
  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s || !funds.data) return [];
    const have = new Set(lines.map((l) => l.symbol));
    return funds.data.funds.filter((f) => !have.has(f.symbol) && (f.name.toLowerCase().includes(s)
      || (f.apir_code || '').toLowerCase().includes(s) || f.symbol.toLowerCase().includes(s))).slice(0, 25);
  }, [q, funds.data, lines]);

  // Live server-side validation, debounced (the note is required, so it is checked too).
  const payload = useMemo(() => ({
    effective_date: date,
    note,
    lines: lines.map((l) => ({ symbol: l.symbol, weight_pct: num(l.weight) ?? 0 })),
  }), [date, note, lines]);
  // feeVersion re-runs the checks after a fee is changed (the fees come back with them).
  const [feeVersion, setFeeVersion] = useState(0);
  const bodyJson = JSON.stringify(payload);
  const payloadKey = `${code}|${feeVersion}|${bodyJson}`;
  const [check, setCheck] = useState({ key: null, data: null, error: null });
  useEffect(() => {
    if (!code || !base) return undefined;
    let live = true;
    const t = setTimeout(() => {
      api.post(`${API}/portfolios/${code}/validate`, JSON.parse(bodyJson))
        .then((r) => { if (live) setCheck({ key: payloadKey, data: r.data, error: null }); })
        .catch((e) => { if (live) setCheck({ key: payloadKey, data: null, error: apiError(e) }); });
    }, 350);
    return () => { live = false; clearTimeout(t); };
  }, [code, base, bodyJson, payloadKey]);
  const v = check.key === payloadKey ? check.data : null;
  const checking = check.key !== payloadKey;
  // Fee per line from the latest checks (kept while a re-check runs, so the column doesn't flicker).
  const feeInfo = useMemo(() => Object.fromEntries((check.data?.lines || []).map((l) => [l.tr_code, l])),
    [check.data]);
  const feesChanged = () => { setFeeVersion((x) => x + 1); onFeesChanged(); };

  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const bookName = analyst.find((b) => b.code === code)?.name || code;
  const save = () => {
    setSaving(true);
    setSaveMsg(null);
    api.post(`${API}/portfolios/${code}/sets`, payload)
      .then((r) => {
        setSaveMsg({ kind: 'ok', text: `Saved ${bookName} from ${fmtDate(r.data.effective_date)}`
          + `${r.data.replaced_set_id ? ' (it replaces the set saved earlier for that date, which is kept in history)' : ''}.` });
        setDraft(null);
        setReload((x) => x + 1);
        onSaved();
      })
      .catch((e) => setSaveMsg({ kind: 'error', text: apiError(e) }))
      .finally(() => setSaving(false));
  };

  const groups = useMemo(() => {
    const order = [];
    const by = {};
    lines.forEach((l) => {
      if (!by[l.acg]) { by[l.acg] = []; order.push(l.acg); }
      by[l.acg].push(l);
    });
    return order.map((acg) => ({ acg, lines: by[acg] }));
  }, [lines]);
  const totalNew = lines.reduce((s, l) => s + (num(l.weight) ?? 0), 0);
  const totalCur = lines.reduce((s, l) => s + (l.current ?? 0), 0);
  const badInput = lines.some((l) => num(l.weight) == null);

  return (
    <div className="tab-panel">
      <MandatePanel state={mandate} />

      <h4 className="ap-section-title">Edit a book</h4>
      <BookSelect id="ap-manage-book" value={code} books={analyst}
        onChange={(c) => { setCode(c); setDraft(null); setSaveMsg(null); setQ(''); }} />
      {hist.error && <Notice kind="error">{hist.error}</Notice>}
      {saveMsg && <Notice kind={saveMsg.kind === 'ok' ? 'ok' : 'error'}>{saveMsg.text}</Notice>}
      {base && (
        <div className="ap-manage">
          <div className="ap-manage-meta">
            <label htmlFor="ap-eff-date">Effective date</label>
            <input id="ap-eff-date" type="date" value={date} max={sydneyToday()} min={base.latest || undefined}
              onChange={(e) => edit(() => ({ date: e.target.value }))} />
            <label htmlFor="ap-note">Note (required)</label>
            <input id="ap-note" type="text" value={note} maxLength={1000} required
              className={active && !note.trim() ? 'ap-note-missing' : undefined}
              placeholder="What changed and why — shown in the monthly review on the Changes tab"
              onChange={(e) => edit(() => ({ note: e.target.value }))} />
            <span className="ap-footnote">Current holdings saved from {fmtDate(base.latest)}.</span>
          </div>

          {check.data?.fees?.missing?.length > 0 && (
            <Notice kind="warn">
              No fee in any source for {check.data.fees.missing.join(', ')}. They are left out of Fees &amp; Costs;
              set a fee with ✎ in the Fee column.
            </Notice>
          )}
          <table className="ap-manage-table">
            <thead>
              <tr><th className="ap-l">Fund</th><th className="ap-l">APIR</th><th className="ap-l">TR code</th>
                <th title="Management fees & costs, % p.a. Hover a fee for its source; ✎ sets it in this app">Fee</th>
                <th>Current %</th><th>New %</th><th>Change</th><th /></tr>
            </thead>
            <tbody>
              {groups.map((g, gi) => (
                <React.Fragment key={g.acg}>
                  <tr className="ap-manage-ac">
                    <td colSpan={4}>
                      <span className="asset-class-indicator" style={{ backgroundColor: getAssetClassColor(g.acg, gi) }} />{g.acg}
                    </td>
                    <td>{fmtPct(g.lines.reduce((s, l) => s + (l.current ?? 0), 0), 2)}</td>
                    <td>{fmtPct(g.lines.reduce((s, l) => s + (num(l.weight) ?? 0), 0), 2)}</td>
                    <td />
                    <td />
                  </tr>
                  {g.lines.map((l) => {
                    const n = num(l.weight);
                    const d = n == null ? null : n - (l.current ?? 0);
                    return (
                      <tr key={l.symbol} className={l.current == null ? 'ap-manage-new' : undefined}>
                        <td className="ap-l">{l.name}{l.current == null && <span className="ap-flag ap-flag-new">new</span>}</td>
                        <td className="ap-l">{l.apir || ''}</td>
                        <td className="ap-l">{l.symbol}</td>
                        <FeeCell info={feeInfo[l.symbol]} apir={feeInfo[l.symbol]?.apir_code || l.apir}
                          symbol={l.symbol} onChanged={feesChanged} />
                        <td>{l.current == null ? '–' : fmtPct(l.current, 2)}</td>
                        <td>
                          <input className={`ap-weight-input${n == null ? ' bad' : ''}`} type="number" step="0.01" min="0"
                            value={l.weight}
                            onChange={(e) => edit((cur) => ({ lines: cur.lines.map((x) => (x.symbol === l.symbol ? { ...x, weight: e.target.value } : x)) }))} />
                        </td>
                        <td className={d > 0.00001 ? 'ap-up' : d < -0.00001 ? 'ap-down' : ''}>{d == null ? '–' : fmtPct(d, 2, true)}</td>
                        <td>
                          <button type="button" className="ap-remove" title="Remove this fund"
                            onClick={() => edit((cur) => ({ lines: cur.lines.filter((x) => x.symbol !== l.symbol) }))}>✕</button>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
              <tr className="total-row">
                <td colSpan={4}>Total</td>
                <td>{fmtPct(totalCur, 2)}</td>
                <td className={Math.abs(totalNew - 100) > 0.005 ? 'ap-down' : ''}>{fmtPct(totalNew, 2)}</td>
                <td>{fmtPct(totalNew - totalCur, 2, true)}</td>
                <td />
              </tr>
            </tbody>
          </table>

          <div className="ap-add-fund">
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={funds.data ? `+ Add fund: search ${funds.data.funds.length} AP List funds by name, APIR or TR code`
                : 'Loading the AP List...'} disabled={!funds.data} />
            {funds.error && <Notice kind="error">{funds.error}</Notice>}
            {matches.length > 0 && (
              <ul className="ap-fund-results">
                {matches.map((f) => (
                  <li key={f.symbol}>
                    <button type="button" onClick={() => {
                      edit((cur) => ({ lines: [...cur.lines, { symbol: f.symbol, name: f.name, apir: f.apir_code,
                        acg: f.asset_class_group, current: null, weight: '' }] }));
                      setQ('');
                    }}>
                      <span className="ap-fund-name">{f.name}</span>
                      <span className="ap-fund-meta">{f.apir_code || '—'} · {f.symbol} · {f.sub_asset_class || 'no SAC'} · {f.asset_class_group}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="ap-checks">
            <h4 className="ap-section-title">Checks {checking && <span className="ap-footnote">(checking…)</span>}</h4>
            {badInput && <Notice kind="warn">Every weight must be a number; remove a fund rather than blanking it.</Notice>}
            {check.error && check.key === payloadKey && <Notice kind="error">{check.error}</Notice>}
            {v && (
              <>
                {v.errors.length > 0 && <Notice kind="error">{v.errors.join(' · ')}</Notice>}
                {active && v.note_error && <Notice kind="warn">{v.note_error}.</Notice>}
                {v.check.errors.length > 0 && <Notice kind="error">{v.check.errors.join(' · ')}</Notice>}
                {v.check.breaches.length > 0 && (
                  <Notice kind="error">Outside the ±{v.check.limit_pp.toFixed(2)}pp DS70 limits: {v.check.breaches.join(' · ')}</Notice>
                )}
                {v.ok && <Notice kind="ok">All checks pass.</Notice>}
                {v.fees && (
                  <p className="ap-footnote">
                    Fees &amp; Costs (wtd) {fmtPct(v.fees.fees_costs_wtd, 2)} · Net (wtd) {fmtPct(v.fees.net_wtd, 2)} for
                    these weights{v.fees.missing.length > 0
                      ? `, leaving out ${fmtPct(v.fees.missing_weight, 2)} with no fee (${v.fees.missing.join(', ')})` : ''}.
                  </p>
                )}
                {!active && !v.errors.length && v.check.ok && (
                  <Notice kind="ok">The saved holdings are within the DS70 limits.</Notice>
                )}
                {v.replaces_same_day_set && (
                  <Notice kind="warn">A set is already saved for {fmtDate(date)}; saving replaces it (the old one is kept in history).</Notice>
                )}
                <CheckTable check={v.check} />
              </>
            )}
          </div>

          <div className="ap-manage-actions">
            <button type="button" className="select-all-btn" disabled={!active || saving} onClick={() => { setDraft(null); setSaveMsg(null); }}>
              Discard changes
            </button>
            <button type="button" className="select-all-btn primary"
              disabled={!active || !v?.ok || saving || badInput || !note.trim()}
              title={!active ? 'No changes yet' : !note.trim() ? 'Add a note saying what changed and why'
                : !v?.ok ? 'Fix the failed checks first' : undefined}
              onClick={save}>
              {saving ? 'Saving…' : `Save ${bookName} from ${fmtDate(date)}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
